import { cn } from 'common/utils';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'components/v2/atoms/pagination';

interface PaginationSectionProps {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalCount?: number;
  pageCount?: number;
  perPage: number;
  isLoading?: boolean;
  className?: string;
}

const PaginationSection = ({
  currentPage,
  setCurrentPage,
  totalCount,
  pageCount,
  perPage,
  isLoading,
  className = '',
}: PaginationSectionProps) => {
  const totalPage = pageCount
    ? pageCount
    : Math.ceil((totalCount ?? 0) / perPage);
  if (totalPage < 1) return null;

  return (
    <div
      className={cn(
        'flex justify-center',
        className,
        isLoading ? 'pointer-events-none' : '',
      )}
    >
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              className={
                currentPage <= 1 ? 'cursor-not-allowed opacity-50' : ''
              }
              onClick={() => {
                if (currentPage > 1) setCurrentPage(currentPage - 1);
              }}
            />
          </PaginationItem>
          {/* first page */}
          <RestPageLinks
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPage={totalPage}
          />
          <PaginationItem>
            <PaginationNext
              className={
                currentPage >= totalPage ? 'cursor-not-allowed opacity-50' : ''
              }
              onClick={() => {
                if (currentPage < totalPage) setCurrentPage(currentPage + 1);
              }}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

interface RestPageLinksProps {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPage: number;
}

const RestPageLinks = ({
  currentPage,
  setCurrentPage,
  totalPage,
}: RestPageLinksProps): JSX.Element => {
  if (totalPage < 6) {
    return (
      <>
        {Array.from({ length: totalPage }, (_, i) => i + 1).map((page) => (
          <PaginationItem key={page}>
            <PaginationLink
              isActive={page === currentPage}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}
      </>
    );
  }
  const beforeCurrentPageCount = currentPage - 1;
  const afterCurrentPageCount = totalPage - currentPage;
  return (
    <>
      {currentPage > 2 && (
        <PaginationItem>
          <PaginationLink
            isActive={currentPage === 1}
            onClick={() => setCurrentPage(1)}
          >
            1
          </PaginationLink>
        </PaginationItem>
      )}

      {beforeCurrentPageCount > 2 && <span>...</span>}
      {currentPage > 1 && (
        <PaginationItem>
          <PaginationLink
            isActive={currentPage === currentPage - 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            {currentPage - 1}
          </PaginationLink>
        </PaginationItem>
      )}
      <PaginationItem>
        <PaginationLink
          isActive={currentPage === currentPage}
          onClick={() => setCurrentPage(currentPage)}
        >
          {currentPage}
        </PaginationLink>
      </PaginationItem>
      {currentPage < totalPage && (
        <PaginationItem>
          <PaginationLink
            isActive={currentPage === currentPage + 1}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            {currentPage + 1}
          </PaginationLink>
        </PaginationItem>
      )}
      {afterCurrentPageCount > 2 && <span>...</span>}
      {afterCurrentPageCount > 1 && totalPage > 1 && (
        <PaginationItem>
          <PaginationLink
            isActive={currentPage === totalPage}
            onClick={() => setCurrentPage(totalPage)}
          >
            {totalPage}
          </PaginationLink>
        </PaginationItem>
      )}
    </>
  );
};
export default PaginationSection;
